import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import Button from '../components/Button';
import FormField, { Input } from '../components/FormField';

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_CFG = {
  open:      { label: 'Offen',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed:    { label: 'Geschlossen', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled: { label: 'Abgesagt',    cls: 'bg-red-50 text-red-600 border-red-200' },
};

const EMPTY = {
  restaurant_name: '', restaurant_url: '', menu_url: '',
  departure_time: '', order_deadline: '', location: '',
  paypal_info: '', accepts_cash: true,
};

export default function FoodRuns() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/api/food-runs').then((r) => setRuns(r.data));
  useEffect(() => { load(); }, []);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      ['restaurant_url', 'menu_url', 'location', 'paypal_info'].forEach((k) => { if (!payload[k]) payload[k] = null; });
      const res = await api.post('/api/food-runs', payload);
      setForm(EMPTY);
      navigate(`/food-runs/${res.data.id}`);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler beim Erstellen'); }
    finally { setSaving(false); }
  };

  if (runs === null) {
    return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="page-title mb-0">🍔 Food for Friends</h1>
          <p className="text-sm text-slate-500 mt-1">Wer holt Essen? Organisiert gemeinsame Bestellungen und Abholfahrten.</p>
        </div>
        <Button onClick={() => setCreating((v) => !v)}>{creating ? 'Abbrechen' : '+ Food Run starten'}</Button>
      </div>

      {creating && (
        <div className="card border border-ocean-200 mb-6">
          <h3 className="section-title">Neuer Food Run</h3>
          {error && <div className="alert-error mb-4">{error}</div>}
          <form onSubmit={save}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <FormField label="Restaurant" required className="sm:col-span-2"><Input value={form.restaurant_name} onChange={set('restaurant_name')} required placeholder="z.B. Pizza Roma" /></FormField>
              <FormField label="Restaurant-Link"><Input value={form.restaurant_url} onChange={set('restaurant_url')} placeholder="https://..." /></FormField>
              <FormField label="Speisekarte-Link"><Input value={form.menu_url} onChange={set('menu_url')} placeholder="https://..." /></FormField>
              <FormField label="Abfahrt" required><Input type="datetime-local" value={form.departure_time} onChange={set('departure_time')} required /></FormField>
              <FormField label="Bestellschluss" required><Input type="datetime-local" value={form.order_deadline} onChange={set('order_deadline')} required /></FormField>
              <FormField label="Treffpunkt / Ort"><Input value={form.location} onChange={set('location')} /></FormField>
              <FormField label="Zahlung"><Input value={form.paypal_info} onChange={set('paypal_info')} placeholder="PayPal-Name oder Hinweis" /></FormField>
            </div>
            <div className="flex gap-2 mt-2">
              <Button type="submit" loading={saving}>Food Run erstellen</Button>
              <Button variant="ghost" type="button" onClick={() => { setCreating(false); setForm(EMPTY); }}>Abbrechen</Button>
            </div>
          </form>
        </div>
      )}

      {runs.length === 0 ? (
        <div className="card text-center py-14">
          <p className="text-slate-400 text-sm">Aktuell ist kein Food Run geplant. Sei der Erste!</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {runs.map((r) => {
            const cfg = STATUS_CFG[r.status] || STATUS_CFG.open;
            return (
              <Link to={`/food-runs/${r.id}`} key={r.id} className="card hover:shadow-card-hover transition-shadow duration-200 flex flex-col no-underline text-inherit">
                <div className="flex justify-between items-start gap-2 mb-2">
                  <h3 className="font-bold text-ocean-900 text-base leading-tight">{r.restaurant_name}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border shrink-0 ${cfg.cls}`}>{cfg.label}</span>
                </div>
                <p className="text-sm text-slate-500 mb-0.5">🚗 Abfahrt: {fmt(r.departure_time)}</p>
                <p className="text-sm text-slate-500 mb-2">⏰ Bestellschluss: {fmt(r.order_deadline)}</p>
                {r.location && <p className="text-sm text-slate-500 mb-2">📍 {r.location}</p>}
                <p className="text-xs text-slate-400 mt-auto pt-2 border-t border-slate-100">
                  {r.items.length} Bestellung{r.items.length !== 1 ? 'en' : ''} · von {r.creator?.nickname || r.creator?.username || '–'}
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
